import clsx from 'clsx';
import AirHandlerIcon from 'components/icons/air-handler-icon';
import EvaporatorCoilIcon from 'components/icons/evaporator-coil-icon';
import FurnaceIcon from 'components/icons/furnace-icon';
import ModularBlowerIcon from 'components/icons/modular-blower-icon';
import OutdoorUnitIcon from 'components/icons/outdoor-unit-icon';
import { ComponentType, SVGProps } from 'react';
import {
  EQUIPMENT_PLACEHOLDER_KIND_LABELS,
  EquipmentPlaceholderKind
} from 'utils/equipment-placeholder-kind';

type EquipmentPlaceholderPhotoSize = 'small' | 'medium' | 'thumb';

const PLACEHOLDER_ICONS: Record<
  EquipmentPlaceholderKind,
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  outdoorUnit: OutdoorUnitIcon,
  evaporatorCoil: EvaporatorCoilIcon,
  airHandler: AirHandlerIcon,
  furnace: FurnaceIcon,
  modularBlower: ModularBlowerIcon
};

const BOX_CLASSES: Record<EquipmentPlaceholderPhotoSize, string> = {
  small: 'h-8 w-12 rounded-sm',
  medium: 'h-14 w-20 rounded-md',
  thumb: 'h-10 w-10 rounded-md border border-gray-200'
};

const SURFACE_CLASSES: Record<
  'muted' | 'white',
  Record<EquipmentPlaceholderPhotoSize, string>
> = {
  muted: {
    small: 'bg-gray-100',
    medium: 'bg-gray-100',
    thumb: 'bg-gray-50'
  },
  white: {
    small: 'bg-white',
    medium: 'bg-white',
    thumb: 'bg-white'
  }
};

const ICON_CLASSES: Record<EquipmentPlaceholderPhotoSize, string> = {
  small: 'h-5 w-5',
  medium: 'h-8 w-8',
  thumb: 'h-6 w-6'
};

type EquipmentPlaceholderPhotoProps = Readonly<{
  kind: EquipmentPlaceholderKind;
  size?: EquipmentPlaceholderPhotoSize;
  surface?: 'muted' | 'white';
  bare?: boolean;
}>;

export default function EquipmentPlaceholderPhoto({
  kind,
  size = 'medium',
  surface = 'muted',
  bare = false
}: EquipmentPlaceholderPhotoProps) {
  const Icon = PLACEHOLDER_ICONS[kind];

  if (bare) {
    return (
      <Icon
        role='img'
        aria-label={EQUIPMENT_PLACEHOLDER_KIND_LABELS[kind]}
        className={clsx('text-gray-400', ICON_CLASSES[size])}
      />
    );
  }

  return (
    <div
      role='img'
      aria-label={EQUIPMENT_PLACEHOLDER_KIND_LABELS[kind]}
      className={clsx(
        'flex shrink-0 items-center justify-center',
        BOX_CLASSES[size],
        SURFACE_CLASSES[surface][size]
      )}
    >
      <Icon className={clsx('text-gray-400', ICON_CLASSES[size])} />
    </div>
  );
}
